import PropTypes from 'prop-types';
import { IMaskInput } from 'react-imask';

const TIME_MASK = '00:00:00';

/**
 * Masked input for time values in the format **HH:MM:SS**.
 * Use as `inputComponent` on MUI TextField's input slot.
 *
 * @example
 * <TextField
 *   label="Estimate (HH:MM:SS)"
 *   name="estimate"
 *   value={value}
 *   onChange={handleChange}
 *   slotProps={{
 *     input: { inputComponent: TimeMaskInput },
 *   }}
 * />
 *
 * @param {object} props
 * @param {import("prop-types").InferProps<typeof TimeMaskInputPropTypes>["name"]} props.name Field name returned in the synthetic event.
 * @param {React.Ref<HTMLInputElement>} [props.ref]
 * @param {((event: { target: { name: string | undefined, value: string } }) => void) | null} [props.onChange]
 * @param {React.FocusEventHandler<HTMLInputElement> | null} [props.onBlur]
 * @param {React.KeyboardEventHandler<HTMLInputElement> | null} [props.onKeyDown]
 */
export default function TimeMaskInput({
  onBlur = null,
  onChange = null,
  onKeyDown = null,
  name,
  ref,
  ...other
}) {
  /** Completes a half-typed time from where the caret was: `1` to `10:00:00`, `00:5` to `00:50:00` */
  const fillMissingDigits = value => {
    // An empty value is left empty rather than turned into a zero time
    const paddedValue = value ? value + TIME_MASK.slice(value.length) : value;

    if (paddedValue !== value) {
      onChange?.({ target: { name, value: paddedValue } });
    }
  };

  return (
    <IMaskInput
      name={name}
      {...other}
      overwrite
      inputMode="numeric"
      mask={TIME_MASK}
      placeholder={TIME_MASK}
      inputRef={ref}
      onAccept={value => onChange?.({ target: { name, value } })}
      onBlur={event => {
        fillMissingDigits(event.target.value);
        // The consumer's own handler, such as the focus tracking of the surrounding MUI input
        onBlur?.(event);
      }}
      onKeyDown={event => {
        if (event.key === 'Enter') {
          fillMissingDigits(event.currentTarget.value);
        }
        onKeyDown?.(event);
      }}
    />
  );
}

const TimeMaskInputPropTypes = {
  name: PropTypes.string.isRequired,
  onBlur: PropTypes.func,
  onChange: PropTypes.func,
  onKeyDown: PropTypes.func,
  ref: PropTypes.oneOfType([PropTypes.func, PropTypes.object]).isRequired,
};
TimeMaskInput.propTypes = TimeMaskInputPropTypes;
